import { Link, useSearchParams } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import Nav from "@/components/Nav";
import GoogleButton from "@/components/GoogleButton";
import AnimatedPuppy from "@/components/AnimatedPuppy";
import { CheckCircle, Envelope } from "@phosphor-icons/react";

export default function Welcome() {
    const { t } = useI18n();
    const [params] = useSearchParams();
    // Guest visitors who just paid land here with ?paddle=success&guest=1 so
    // we can show a friendly "check your inbox" panel instead of the standard
    // sign-in card. Their account was auto-provisioned by the Paddle webhook
    // and a magic sign-in link is on its way via Resend.
    const paddleGuestSuccess = params.get("paddle") === "success" && params.get("guest") === "1";

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-paw-night">
            <Nav />

            <div aria-hidden className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-24 -start-24 w-[420px] h-[420px] rounded-full bg-paw-terra/12 dark:bg-paw-terra/[0.06] blur-[120px]" />
                <div className="absolute bottom-0 -end-32 w-[520px] h-[520px] rounded-full bg-paw-terra/8 dark:bg-paw-terra/[0.04] blur-[140px]" />
            </div>

            <main className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
                {paddleGuestSuccess ? (
                    /* Post-payment success panel for guest checkouts. No sign-in
                       controls here — the user's magic sign-in link has already
                       been emailed by the backend and they should follow that. */
                    <div className="w-full max-w-[440px] mx-auto text-center" data-testid="paddle-guest-success">
                        <div className="relative mx-auto mb-6 flex items-center justify-center h-24 w-24 rounded-full bg-emerald-500/15 dark:bg-emerald-400/10">
                            <CheckCircle size={54} weight="fill" className="text-emerald-500" />
                        </div>
                        <h1 className="font-display text-[30px] sm:text-[38px] leading-[1.08] font-medium tracking-tight text-paw-ink dark:text-white">
                            {t("paddle_success.headline")}
                        </h1>
                        <p className="mt-4 text-[15px] leading-relaxed text-paw-ink2 dark:text-paw-silver">
                            {t("paddle_success.body")}
                        </p>
                        <div className="mt-8 mx-auto flex items-center justify-center gap-2 rounded-2xl border border-paw-line dark:border-white/10 bg-white/70 dark:bg-white/[0.04] backdrop-blur-md px-5 py-4 text-left">
                            <Envelope size={22} weight="fill" className="text-paw-terra flex-shrink-0" />
                            <p className="text-[13px] leading-snug text-paw-ink dark:text-paw-bone/80">
                                {t("paddle_success.check_inbox")}
                            </p>
                        </div>
                        <p className="mt-6 text-xs text-paw-ink2/70 dark:text-paw-silver/60 leading-relaxed">
                            {t("paddle_success.no_email")}
                            {" "}
                            <Link to="/support" className="text-paw-terra hover:text-paw-terraDark font-semibold" data-testid="paddle-success-support">
                                {t("paddle_success.contact_support")}
                            </Link>
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-1.5 mt-10 text-sm text-paw-silver hover:text-paw-ink dark:hover:text-white transition"
                            data-testid="paddle-success-back-home"
                        >
                            <span aria-hidden>←</span> PawLink
                        </Link>
                    </div>
                ) : (
                <div className="w-full max-w-[380px] mx-auto text-center" data-testid="welcome-card">
                    {/* Cute animated puppy — restored per user request */}
                    <div className="relative mx-auto mb-8 flex items-center justify-center" data-testid="welcome-hero">
                        <div aria-hidden className="absolute inset-0 -m-6 rounded-full bg-paw-terra/20 blur-[70px] paw-pulse" />
                        <div className="relative"><AnimatedPuppy size={180} /></div>
                    </div>

                    <p className="text-sm text-paw-silver tracking-wide mb-2">
                        {t("auth.welcome_hi")}
                    </p>
                    <h1 className="font-display text-[34px] sm:text-[42px] leading-[1.05] font-medium tracking-tight text-paw-ink dark:text-white">
                        {t("auth.welcome_h")}
                    </h1>
                    <p className="mt-3 text-[15px] text-paw-ink2 dark:text-paw-silver">
                        {t("auth.welcome_sub")}
                    </p>

                    <div className="mt-10">
                        <GoogleButton label={t("auth.google_signin")} testId="welcome-google-btn" />
                    </div>

                    <p className="mt-6 text-xs text-paw-ink2/70 dark:text-paw-silver/60 leading-relaxed max-w-[280px] mx-auto">
                        {t("auth.welcome_footer")}
                    </p>

                    <Link to="/" className="inline-flex items-center gap-1.5 mt-8 text-sm text-paw-silver hover:text-white transition"
                        data-testid="welcome-back-home">
                        <span aria-hidden>←</span> PawLink
                    </Link>
                </div>
                )}
            </main>
        </div>
    );
}
